import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "input",
    "dropzone",
    "progress",
    "progressBar",
    "progressText",
    "fileInfo",
    "fileName",
    "fileSize",
    "uploadSpeed",
    "timeRemaining",
    "submitButton",
    "cancelButton",
    "errorMessage"
  ]

  static values = {
    url: String,
    maxSize: { type: Number, default: 104857600 }, // 100MB default
    acceptedTypes: { type: Array, default: ["application/pdf"] }
  }

  connect() {
    console.log('DirectUpload controller connected')
    console.log('Has input target:', this.hasInputTarget)
    console.log('Has dropzone target:', this.hasDropzoneTarget)

    this.uploadInProgress = false
    this.startTime = null
    this.uploadedBytes = 0
    this.totalBytes = 0
    this.currentUpload = null

    // Set up drag and drop
    this.setupDragAndDrop()
  }

  setupDragAndDrop() {
    if (!this.hasDropzoneTarget) return

    // Prevent default drag behaviors on dropzone
    this.boundDragEnter = this.handleDragEnter.bind(this)
    this.boundDragOver = this.handleDragOver.bind(this)
    this.boundDragLeave = this.handleDragLeave.bind(this)
    this.boundDrop = this.handleDrop.bind(this)

    this.dropzoneTarget.addEventListener('dragenter', this.boundDragEnter)
    this.dropzoneTarget.addEventListener('dragover', this.boundDragOver)
    this.dropzoneTarget.addEventListener('dragleave', this.boundDragLeave)
    this.dropzoneTarget.addEventListener('drop', this.boundDrop)
  }

  handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropzoneTarget.classList.add('border-indigo-500', 'bg-indigo-50')
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropzoneTarget.classList.add('border-indigo-500', 'bg-indigo-50')
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    // Only remove highlight if leaving the dropzone itself, not child elements
    if (e.target === this.dropzoneTarget) {
      this.dropzoneTarget.classList.remove('border-indigo-500', 'bg-indigo-50')
    }
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropzoneTarget.classList.remove('border-indigo-500', 'bg-indigo-50')

    const files = e.dataTransfer.files
    if (files.length > 0) {
      // Create a new DataTransfer to set files on the input
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(files[0])
      this.inputTarget.files = dataTransfer.files
      this.handleFileSelect({ target: this.inputTarget })
    }
  }

  openFileDialog(event) {
    console.log('openFileDialog called')
    event.preventDefault()
    event.stopPropagation()
    this.inputTarget.click()
  }

  handleFileSelect(event) {
    console.log('handleFileSelect called')
    const file = event.target.files[0]
    console.log('File selected:', file)

    if (!file) return

    // Validate file type
    if (!this.acceptedTypesValue.includes(file.type)) {
      this.showError(`Please select a valid file. Accepted types: ${this.acceptedTypesValue.join(', ')}`)
      this.resetInput()
      return
    }

    // Validate file size
    if (file.size > this.maxSizeValue) {
      this.showError(`File size exceeds maximum allowed size of ${this.formatFileSize(this.maxSizeValue)}`)
      this.resetInput()
      return
    }

    // Clear any previous errors
    this.clearError()

    // Show file info
    this.displayFileInfo(file)

    // Start upload
    this.uploadFile(file)
  }

  displayFileInfo(file) {
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
    }

    if (this.hasFileSizeTarget) {
      this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    }

    if (this.hasFileInfoTarget) {
      this.fileInfoTarget.classList.remove('hidden')
    }

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('hidden')
    }
  }

  uploadFile(file) {
    this.uploadInProgress = true
    this.startTime = Date.now()
    this.totalBytes = file.size
    this.uploadedBytes = 0

    // Show progress section
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('hidden')
    }

    // Disable submit button during upload
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }

    // Show cancel button
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.remove('hidden')
    }

    // Get the direct upload URL
    const url = this.urlValue || this.inputTarget.dataset.directUploadUrl

    // Create a new DirectUpload instance
    const upload = new DirectUpload(file, url, this)
    this.currentUpload = upload

    // Start the upload
    upload.create((error, blob) => {
      if (error) {
        this.showError(`Upload failed: ${error}`)
        this.resetUpload()
      } else {
        // Create a hidden input with the signed blob ID
        this.createBlobInput(blob.signed_id)

        // Mark upload as complete
        this.uploadComplete()
      }
    })
  }

  // DirectUpload delegate methods
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      this.uploadProgressChanged(event)
    })
  }

  uploadProgressChanged(event) {
    const percentage = Math.round((event.loaded / event.total) * 100)
    this.uploadedBytes = event.loaded

    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }

    // Update progress text with combined message
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `Uploading... ${percentage}%`
    }

    // Calculate and display upload speed
    if (this.hasUploadSpeedTarget) {
      const elapsed = (Date.now() - this.startTime) / 1000 // seconds
      const speed = this.uploadedBytes / elapsed // bytes per second
      this.uploadSpeedTarget.textContent = `${this.formatFileSize(speed)}/s`
    }

    // Calculate and display time remaining
    if (this.hasTimeRemainingTarget) {
      const elapsed = (Date.now() - this.startTime) / 1000
      const speed = this.uploadedBytes / elapsed
      const remaining = (this.totalBytes - this.uploadedBytes) / speed
      this.timeRemainingTarget.textContent = this.formatTime(remaining)
    }
  }

  uploadComplete() {
    this.uploadInProgress = false

    // Update UI to show completion
    if (this.hasProgressBarTarget) {
      // Remove indigo/purple gradient classes
      this.progressBarTarget.classList.remove('bg-indigo-600', 'from-indigo-500', 'to-purple-500')
      // Add green gradient classes
      this.progressBarTarget.classList.add('from-green-500', 'to-emerald-500')
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = 'Upload complete! ✓'
      this.progressTextTarget.classList.remove('text-gray-900')
      this.progressTextTarget.classList.add('text-green-600', 'font-semibold')
    }

    // Hide speed and time remaining
    if (this.hasUploadSpeedTarget && this.hasUploadSpeedTarget.parentElement) {
      this.uploadSpeedTarget.parentElement.classList.add('hidden')
    }

    if (this.hasTimeRemainingTarget && this.hasTimeRemainingTarget.parentElement) {
      this.timeRemainingTarget.parentElement.classList.add('hidden')
    }

    // Hide cancel button
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.add('hidden')
    }

    // Enable submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  cancelUpload(event) {
    event.preventDefault()

    if (this.currentUpload && this.currentUpload.xhr) {
      this.currentUpload.xhr.abort()
    }

    this.showError('Upload cancelled')
    this.resetUpload()
  }

  createBlobInput(signedId) {
    // Remove any existing blob input
    const existingInput = this.element.querySelector('input[name*="[blob_signed_id]"]')
    if (existingInput) {
      existingInput.remove()
    }

    // Create new hidden input with the signed blob ID
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'

    // Get the field name from the file input
    const fieldName = this.inputTarget.name.replace('[pdf_file]', '[blob_signed_id]')
    hiddenInput.name = fieldName
    hiddenInput.value = signedId

    // Append to form
    this.element.appendChild(hiddenInput)

    // Clear the file input
    this.inputTarget.value = ''
  }

  resetUpload() {
    this.uploadInProgress = false
    this.currentUpload = null

    // Hide progress
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }

    // Show dropzone
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('hidden')
    }

    // Hide file info
    if (this.hasFileInfoTarget) {
      this.fileInfoTarget.classList.add('hidden')
    }

    // Hide cancel button
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.add('hidden')
    }

    // Reset progress
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '0%'
      // Remove green gradient classes
      this.progressBarTarget.classList.remove('from-green-500', 'to-emerald-500')
      // Restore indigo/purple gradient classes
      this.progressBarTarget.classList.add('from-indigo-500', 'to-purple-500')
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = 'Uploading... 0%'
      this.progressTextTarget.classList.remove('text-green-600', 'font-semibold')
      this.progressTextTarget.classList.add('text-gray-900')
    }

    // Show speed and time remaining
    if (this.hasUploadSpeedTarget) {
      this.uploadSpeedTarget.parentElement.classList.remove('hidden')
    }

    if (this.hasTimeRemainingTarget) {
      this.timeRemainingTarget.parentElement.classList.remove('hidden')
    }

    // Reset input
    this.resetInput()
  }

  resetInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      // Find the p tag inside the error message div
      const errorText = this.errorMessageTarget.querySelector('p')
      if (errorText) {
        errorText.textContent = message
      }
      this.errorMessageTarget.classList.remove('hidden')
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      const errorText = this.errorMessageTarget.querySelector('p')
      if (errorText) {
        errorText.textContent = ''
      }
      this.errorMessageTarget.classList.add('hidden')
    }
  }

  // Utility functions
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return 'Calculating...'

    if (seconds < 60) {
      return `${Math.round(seconds)}s`
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60)
      const secs = Math.round(seconds % 60)
      return `${minutes}m ${secs}s`
    } else {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return `${hours}h ${minutes}m`
    }
  }

  disconnect() {
    // Clean up if component is removed during upload
    if (this.uploadInProgress && this.currentUpload) {
      this.currentUpload.xhr.abort()
    }

    // Remove drag and drop event listeners
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.removeEventListener('dragenter', this.boundDragEnter)
      this.dropzoneTarget.removeEventListener('dragover', this.boundDragOver)
      this.dropzoneTarget.removeEventListener('dragleave', this.boundDragLeave)
      this.dropzoneTarget.removeEventListener('drop', this.boundDrop)
    }
  }
}
